import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Escritório que transmite solidez e confiança ao cliente',
    description: 'O cliente que contrata um contador está confiando suas finanças a ele. Um escritório bem projetado — organizado, sóbrio e profissional — reforça essa confiança antes de qualquer reunião começar.',
  },
  {
    icon: 'ri-archive-line',
    title: 'Arquivos e armários que organizam anos de documentação',
    description: 'Escritórios de contabilidade acumulam documentos fiscais, contratos e declarações por anos. Armários de arquivo com capacidade real, organização por cliente e fácil acesso são essenciais para a eficiência da equipe.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do escritório completo',
    description: 'Recepção, sala de atendimento, área operacional e arquivo projetados em 3D. Você aprova o layout antes de fabricar — e garante que cada metro quadrado seja aproveitado.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem fora do horário de atendimento',
    description: 'Nossa equipe instala nos horários que não atrapalham o atendimento aos clientes — fins de semana ou após o expediente. Sem interromper a operação do escritório.',
  },
];

const blocks = [
  {
    title: 'Recepção e Sala de Atendimento que Transmitem Credibilidade',
    text: [
      'O cliente que chega ao escritório de contabilidade está preocupado com impostos, folha de pagamento ou declaração de IR. Ele precisa sentir que está em boas mãos desde o momento em que entra. Uma recepção organizada, com balcão bem projetado e cadeiras confortáveis, transmite a seriedade e a competência que ele espera do profissional que vai cuidar das suas finanças.',
      'Nossos projetos para escritórios de contabilidade incluem balcão de recepção com tampo em granito ou laminado HPL, espaço interno organizado para computador e documentos, cadeiras de espera em courvin resistente ao uso intenso, sala de atendimento com mesa de reunião para 4 a 6 pessoas, cadeiras executivas confortáveis, e painel de fundo com o logo do escritório em relevo. Tudo para que o cliente saia da reunião com a sensação de que seu patrimônio está em mãos competentes.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Professional%20accounting%20office%20reception%20area%20with%20organized%20desk%2C%20comfortable%20waiting%20chairs%2C%20clean%20modern%20design%2C%20neutral%20tones%2C%20credibility%20and%20trust%20interior%2C%20warm%20lighting%2C%20professional%20photography&width=800&height=500&seq=cont_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Estações de Trabalho Ergonômicas para a Equipe Operacional',
    text: [
      'A equipe de contabilidade passa horas na frente do computador analisando planilhas, lançando notas fiscais e preparando declarações. Uma estação de trabalho mal projetada — mesa na altura errada, cadeira sem apoio lombar, monitor posicionado incorretamente — gera dores, cansaço e queda de produtividade. E produtividade em contabilidade é prazo cumprido e cliente satisfeito.',
      'Projetamos estações de trabalho para escritórios de contabilidade com mesas em MDF de alta espessura com tampo em laminado HPL resistente a riscos, altura regulável ou na medida certa para o monitor e o teclado, passa-fios integrado para organizar cabos de computador e carregadores, divisórias em vidro temperado ou MDF que criam privacidade sem isolar a equipe, e cadeiras ergonômicas com regulagem de altura, apoio lombar ajustável e braços reguláveis.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20accounting%20office%20workstations%20with%20organized%20desks%2C%20ergonomic%20chairs%2C%20computer%20monitors%2C%20clean%20professional%20environment%2C%20neutral%20tones%2C%20efficient%20workspace%20design%2C%20warm%20lighting&width=800&height=500&seq=cont_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Arquivo e Sala de Documentos que Organizam Anos de Histórico',
    text: [
      'Um escritório de contabilidade guarda documentos fiscais por 5 anos, contratos por 10 anos e alguns documentos por prazo indeterminado. Sem um sistema de arquivo bem projetado, a equipe perde tempo procurando documentos, corre risco de perder prazos e expõe informações confidenciais dos clientes.',
      'Projetamos salas de arquivo para escritórios de contabilidade com armários de arquivo em MDF de alta espessura com gavetas deslizantes para pastas suspensas, capacidade para centenas de pastas organizadas por cliente e ano, fechadura com chave para documentos confidenciais, prateleiras abertas para acesso rápido aos documentos de uso frequente, e identificação visual por cores para facilitar a localização. Tudo para que a equipe encontre qualquer documento em menos de 2 minutos.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Professional%20office%20archive%20room%20with%20organized%20filing%20cabinets%2C%20document%20storage%2C%20clean%20systematic%20design%2C%20neutral%20tones%2C%20efficient%20document%20management%20space%2C%20warm%20lighting&width=800&height=500&seq=cont_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Professional%20accounting%20office%20reception%20with%20organized%20desk%2C%20clean%20modern%20design%2C%20neutral%20tones%2C%20credibility%20interior&width=600&height=400&seq=cont_g1&orientation=landscape',
    alt: 'Recepção de escritório de contabilidade',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20accounting%20office%20meeting%20room%20with%20conference%20table%2C%20executive%20chairs%2C%20professional%20design%2C%20warm%20lighting&width=600&height=400&seq=cont_g2&orientation=landscape',
    alt: 'Sala de atendimento com mesa de reunião',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Accounting%20office%20workstations%20with%20organized%20desks%2C%20ergonomic%20chairs%2C%20monitors%2C%20clean%20professional%20environment&width=600&height=400&seq=cont_g3&orientation=landscape',
    alt: 'Estações de trabalho ergonômicas para equipe',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Office%20archive%20room%20with%20organized%20filing%20cabinets%2C%20document%20storage%2C%20systematic%20design%2C%20neutral%20tones&width=600&height=400&seq=cont_g4&orientation=landscape',
    alt: 'Sala de arquivo com armários organizados',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Contabilidade%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function ContabilidadePage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Escritório de Contabilidade Caruaru PE | Home Office Premium',
    description: 'Mobiliário para escritórios de contabilidade em Caruaru PE. Recepção, sala de atendimento, estações de trabalho e arquivo. Projeto 3D gratuito e montagem fora do horário de atendimento. Atendemos contadores no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário escritório contabilidade Recife, móveis contador Petrolina, mesa escritório contábil Garanhuns, armário arquivo contabilidade Caruaru, mobiliário escritório fiscal Agreste PE, mesa contador Pernambuco, cadeira ergonômica contabilidade Nordeste, arquivo contabilidade Recife, mobiliário contabilidade Caruaru',
    path: '/contabilidade',
  });

  useSchemaLD({
    id: 'schema-contabilidade',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Escritórios de Contabilidade | Caruaru PE',
      description: 'Recepção, sala de atendimento, estações de trabalho e arquivo para escritórios de contabilidade. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/contabilidade`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Escritórios de Contabilidade',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20professional%20accounting%20office%20interior%20with%20organized%20workstations%2C%20clean%20neutral%20design%2C%20warm%20lighting%2C%20credibility%20and%20trust%20atmosphere%2C%20professional%20financial%20office%2C%20wide%20angle%20photography&width=1400&height=700&seq=cont_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Contabilidade · Assessoria Fiscal · Consultoria Financeira
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Escritório que transmite a solidez e a confiança que seus clientes esperam.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Recepção profissional, estações de trabalho ergonômicas e arquivo organizado. Porque o cliente que confia suas finanças a você precisa confiar também no seu ambiente.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem fora do horário de atendimento · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que contadores de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes Contábeis</span> que Transmitem Credibilidade e Organização
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu escritório contábil completo em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Recepção, sala de atendimento, estações de trabalho e arquivo projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem fora do horário de atendimento.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Ambientes de <span className="text-[#E8610A]">Contabilidade Premium</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
